<template>
  <div class="overview-my-commitments flex flex-col gap-4" data-testid="overview-my-commitments-panel">
    <div>
      <h2 class="overview-section-title m-0">
        {{ $t('UserProjects.overview.myCommitments.title') }}
      </h2>
      <p class="overview-section-subline m-0 mt-1">
        {{ weekSubline }}
      </p>
    </div>

    <div class="flex gap-1 flex-wrap">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="overview-tab"
        :class="{ 'overview-tab--active': activeTab === tab.key }"
        :data-testid="`overview-commitments-tab-${tab.key}`"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="filteredCommitments.length === 0" class="overview-my-commitments__empty text-sm text-[var(--color-disabled)] py-8 text-center">
      {{ $t('UserProjects.overview.myCommitments.empty') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <OverviewCommitmentCard
        v-for="item in visibleCommitments"
        :key="item.id"
        :commitment="item"
        @updated="$emit('refresh')"
      />
    </div>

    <button
      type="button"
      class="overview-view-all-link"
      data-testid="overview-view-all-commitments"
      @click="goToMyCommitments"
    >
      {{ $t('UserProjects.overview.myCommitments.viewAllCount', { count: myOpenCommitments.length }) }}
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import OverviewCommitmentCard from './OverviewCommitmentCard.vue';
import {
  filterCommitmentsOverdue,
  filterCommitmentsThisWeek,
  formatOverviewWeekSubline,
} from '@/utils/userProjectsOverview';

const VISIBLE_LIMIT = 5;

export default {
  name: 'OverviewMyCommitmentsPanel',
  components: {
    OverviewCommitmentCard,
  },
  emits: ['refresh'],
  data() {
    return {
      activeTab: 'thisWeek',
    };
  },
  computed: {
    ...mapGetters('pmCommitment', {
      overview: 'overview',
    }),
    myOpenCommitments() {
      return this.overview?.myOpenCommitments || [];
    },
    weekSubline() {
      return formatOverviewWeekSubline(new Date(), this.$t.bind(this));
    },
    tabs() {
      return [
        { key: 'all', label: this.$t('UserProjects.overview.myCommitments.tabs.all') },
        { key: 'thisWeek', label: this.$t('UserProjects.overview.myCommitments.tabs.thisWeek') },
        { key: 'overdue', label: this.$t('UserProjects.overview.myCommitments.tabs.overdue') },
      ];
    },
    filteredCommitments() {
      if (this.activeTab === 'thisWeek') {
        return filterCommitmentsThisWeek(this.myOpenCommitments);
      }
      if (this.activeTab === 'overdue') {
        return filterCommitmentsOverdue(this.myOpenCommitments);
      }
      return this.myOpenCommitments;
    },
    visibleCommitments() {
      return this.filteredCommitments.slice(0, VISIBLE_LIMIT);
    },
  },
  methods: {
    goToMyCommitments() {
      this.$router.push('/user-projects/my-commitments');
    },
  },
};
</script>

<style scoped>
.overview-section-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-dark);
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  flex: 1 1 auto;
}

.overview-section-subline {
  font-size: 13px;
  color: var(--color-disabled);
}

.overview-my-commitments__empty {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}

.overview-tab {
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: var(--color-dark);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.overview-tab--active {
  background: var(--color-orange);
  color: #fff;
}

.overview-tab:not(.overview-tab--active):hover {
  background: #f5f5f5;
}

.overview-view-all-link {
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-orange);
  cursor: pointer;
  text-align: left;
  overflow-wrap: anywhere;
  line-height: 1.35;
  max-width: 100%;
}

.overview-view-all-link:hover {
  text-decoration: underline;
}
</style>
